import styled from 'styled-components';

import { Text } from '@/nova/components/Text/Text';
import { ANIM, COLORS, SPACE } from '@/nova/theme/tokens';

/* A marketing library card's footer stats (Video Ads, Image Banners, Mini-games, HTML5 Banners, Landing Pages): the
   asset's tracking-link figures in equal cells (Reg / FTD / Reg -> FTD, a landing page led by Views), or a quiet hint
   while it has no link,
   under a hairline. Every state (loading, figures, hint) reserves the same slot so the card never jumps when the
   library's stats read lands. */

/** The footer's reserved height: the hint's title and two lines of its text (10px x 1.3, a 2px gap, 2 x 12px x 1.35),
 *  the tallest state, so the skeleton and the figures hold the same box. Layout. */
const FOOT_MIN_H = SPACE[48];

/** The footer slot: a hairline above, then the reserved box. */
export const Foot = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-height: ${FOOT_MIN_H};
  margin-top: ${SPACE[8]};
  padding-top: ${SPACE[8]};
  border-top: 1px solid ${COLORS.hairline};
  box-sizing: content-box;
`;

/* The figures open the asset's stats sheet. The negative margin cancels the padding so the numbers sit exactly where
   they would unwrapped; only the hover wash (same as the card's other quiet controls) reveals the target. */
export const StatsButton = styled.button`
  display: flex;
  gap: ${SPACE[8]};
  width: calc(100% + ${SPACE[16]});
  margin: -${SPACE[4]} -${SPACE[8]};
  padding: ${SPACE[4]} ${SPACE[8]};
  border: 0;
  border-radius: 8px;
  background: transparent;
  color: inherit;
  font: inherit;
  text-align: left;
  cursor: pointer;
  transition: background ${ANIM.INTERACT_MS}ms ${ANIM.EASE};

  &:hover {
    background: ${COLORS.hoverWash};
  }
`;

/** The figures' layout without the button (the loading skeleton). */
export const StatsCells = styled.div`
  display: flex;
  gap: ${SPACE[8]};
`;

export const Stat = styled.div`
  flex: 1 1 0;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 1px; /* a hairline gap between a figure and its label (optical, off the SPACE scale) */
`;

export const StatValue = styled(Text).attrs({ tag: 'span' as const })`
  font-size: 14px;
  font-weight: 700;
  color: ${COLORS.text};
`;

/** A figure's place while it loads: the figure's own font box, so the skeleton line is exactly as tall. */
export const SkeletonValue = styled.span`
  font-size: 14px;
  font-weight: 700;
`;

export const StatLabel = styled(Text).attrs({ tag: 'span' as const })`
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  color: ${COLORS.textFaint};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

/** In place of the figures while the asset has no tracking link: a soft two-line hint at the step that unlocks them. */
/** A landing page with no tracking link yet: its Views beside the hint at what unlocks the sign-ups. */
export const HintRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${SPACE[16]};
  min-width: 0;

  > :first-child {
    flex: 0 0 auto;
  }
`;

export const StatsHint = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACE[2]};
`;

export const StatsHintTitle = styled(Text).attrs({ tag: 'span' as const })`
  font-size: 10px;
  line-height: 1.3;
  font-weight: 600;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  color: ${COLORS.textFaint};
`;

export const StatsHintText = styled(Text).attrs({ tag: 'span' as const })`
  font-size: 12px;
  line-height: 1.35;
  color: ${COLORS.textMuted};
`;
